import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../../utils/apiConfig';

// const API_BASE = 'http://localhost:5000/api/catalog-pg';
const API_BASE = `${API_BASE_URL}/catalog-pg`;
const CACHE_KEY = 'mk_catalog_crud_cache_v1';

export const CATALOG_ENTITIES = [
  'products',
  'brands',
  'categories',
  'employees',
  'outlets',
  'units',
  'stakeholders',
  'warehouses',
  'product-barcodes',
  'images',
  'gst-rates',
  'rate-plans',
  'bills',
];

const canUseStorage = () => typeof window !== 'undefined' && window.localStorage;

const readCatalogCache = () => {
  if (!canUseStorage()) return { data: {}, loadedAtByEntity: {} };

  try {
    const cached = JSON.parse(window.localStorage.getItem(CACHE_KEY) || '{}');
    return {
      data: cached?.data && typeof cached.data === 'object' ? cached.data : {},
      loadedAtByEntity:
        cached?.loadedAtByEntity && typeof cached.loadedAtByEntity === 'object'
          ? cached.loadedAtByEntity
          : {},
    };
  } catch {
    return { data: {}, loadedAtByEntity: {} };
  }
};

const writeCatalogCache = (data, loadedAtByEntity) => {
  if (!canUseStorage()) return;

  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        data,
        loadedAtByEntity,
        savedAt: new Date().toISOString(),
      })
    );
  } catch {}
};

const writeCatalogEntityCache = (entity, rows) => {
  if (!entity) return;

  const cached = readCatalogCache();
  const loadedAt = new Date().toISOString();
  writeCatalogCache(
    { ...cached.data, [entity]: rows },
    { ...cached.loadedAtByEntity, [entity]: loadedAt }
  );
};

const emitCatalogChanged = (entity) => {
  if (typeof window === 'undefined' || !entity) return;

  window.dispatchEvent(
    new CustomEvent('mkcatalog:changed', {
      detail: { entity, changedAt: new Date().toISOString() },
    })
  );
};

const normalizeEntityArg = (arg) =>
  typeof arg === 'string' ? { entity: arg } : { entity: arg?.entity, reason: arg?.reason };

const getConfig = (getState) => {
  const token = getState().posUser?.userInfo?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

export const fetchCatalogEntity = createAsyncThunk(
  'catalogCrud/fetchEntity',
  async (arg, { getState, rejectWithValue }) => {
    try {
      const { entity, reason } = normalizeEntityArg(arg);
      const { data } = await axios.get(`${API_BASE}/${entity}`, getConfig(getState));
      const rows = Array.isArray(data) ? data : data.data || [];
      if (reason === 'catalog-change') {
        writeCatalogEntityCache(entity, rows);
        emitCatalogChanged(entity);
      }
      return { entity, data: rows };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const preloadCatalogEntities = createAsyncThunk(
  'catalogCrud/preloadEntities',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const results = await Promise.allSettled(
        CATALOG_ENTITIES.map((entity) => dispatch(fetchCatalogEntity(entity)).unwrap())
      );

      const failed = results
        .filter((result) => result.status === 'rejected')
        .map((result) => result.reason)
        .filter(Boolean);

      if (failed.length) {
        return rejectWithValue(failed[0]);
      }

      return true;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to load catalogs');
    }
  }
);

export const createCatalogEntity = createAsyncThunk(
  'catalogCrud/createEntity',
  async ({ entity, payload }, { getState, rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(`${API_BASE}/${entity}`, payload, getConfig(getState));
      await dispatch(fetchCatalogEntity({ entity, reason: 'catalog-change' }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const updateCatalogEntity = createAsyncThunk(
  'catalogCrud/updateEntity',
  async ({ entity, id, payload }, { getState, rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.put(`${API_BASE}/${entity}/${id}`, payload, getConfig(getState));
      dispatch(fetchCatalogEntity({ entity, reason: 'catalog-change' }));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const deleteCatalogEntity = createAsyncThunk(
  'catalogCrud/deleteEntity',
  async ({ entity, id }, { getState, rejectWithValue, dispatch }) => {
    try {
      await axios.delete(`${API_BASE}/${entity}/${id}`, getConfig(getState));
      dispatch(fetchCatalogEntity({ entity, reason: 'catalog-change' }));
      return { entity, id };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const cachedCatalog = readCatalogCache();

const catalogCrudSlice = createSlice({
  name: 'catalogCrud',
  initialState: {
    data: cachedCatalog.data,
    loading: false,
    loadingByEntity: {},
    loadedAtByEntity: cachedCatalog.loadedAtByEntity,
    preloaded: false,
    preloading: false,
    error: null,
    successMessage: null,
  },
  reducers: {
    clearCatalogCrudMessage: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher((action) => action.type.startsWith('catalogCrud/') && action.type.endsWith('/pending'), (state, action) => {
        state.loading = true;
        state.error = null;

        const entity = action.meta?.arg?.entity || action.meta?.arg;
        if (entity) {
          state.loadingByEntity[entity] = true;
        } else if (action.type.includes('preloadEntities')) {
          state.preloading = true;
        }
      })
      .addMatcher((action) => action.type.startsWith('catalogCrud/') && action.type.endsWith('/fulfilled'), (state, action) => {
        state.loading = false;

        if (action.type.includes('fetchEntity')) {
          state.data[action.payload.entity] = action.payload.data;
          state.loadingByEntity[action.payload.entity] = false;
          state.loadedAtByEntity[action.payload.entity] = new Date().toISOString();
          writeCatalogCache(state.data, state.loadedAtByEntity);
        } else if (action.type.includes('preloadEntities')) {
          state.preloaded = true;
          state.preloading = false;
        } else {
          const entity = action.meta?.arg?.entity;
          if (entity) {
            state.loadingByEntity[entity] = false;
          }
          state.successMessage = 'Saved successfully';
        }
      })
      .addMatcher((action) => action.type.startsWith('catalogCrud/') && action.type.endsWith('/rejected'), (state, action) => {
        state.loading = false;

        const entity = action.meta?.arg?.entity || action.meta?.arg;
        if (entity) {
          state.loadingByEntity[entity] = false;
        } else if (action.type.includes('preloadEntities')) {
          state.preloading = false;
        }
        state.error = action.payload || 'Something went wrong';
      });
  },
});

export const { clearCatalogCrudMessage } = catalogCrudSlice.actions;
export default catalogCrudSlice.reducer;
